import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { toDatabaseValue } from "@/utils/supabase/type-guards";
import type { Prompt, Category } from "@/lib/types";

const PAGE_SIZE = 12;

export const useSavedPrompts = (userId: string) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const fetchSavedPrompts = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (!userId) return;

    if (pageNum === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // First, get the saved prompt IDs for this user
      const { data: savedData, error: savedError, count } = await supabase
        .from('saved_prompts')
        .select('prompt_id, created_at', { count: 'exact' })
        .eq('user_id', toDatabaseValue(userId))
        .order('created_at', { ascending: false })
        .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1);

      if (savedError) throw savedError;

      if (pageNum === 0 && count !== null) {
        setTotalCount(count);
      }

      if (!savedData || savedData.length === 0) {
        if (reset || pageNum === 0) {
          setPrompts([]);
        }
        setHasMore(false);
        return;
      }

      const promptIds = savedData.map(s => s.prompt_id);

      // Fetch the actual prompts
      const { data: promptsData, error: promptsError } = await supabase
        .from('prompts')
        .select('*')
        .in('id', promptIds);

      if (promptsError) throw promptsError;

      if (!promptsData || promptsData.length === 0) {
        if (reset || pageNum === 0) {
          setPrompts([]);
        }
        setHasMore(false);
        return;
      }

      // Fetch profiles for prompt authors using public_profiles view for security
      const userIds = [...new Set(promptsData.map(p => p.user_id))];
      const { data: profilesData } = await supabase
        .from('public_profiles')
        .select('id, username, avatar_id, badges')
        .in('id', userIds);

      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      // Batch fetch counts and user interactions
      const [commentsResult, likesResult, userLikesResult] = await Promise.all([
        supabase
          .from('prompt_comments')
          .select('prompt_id')
          .in('prompt_id', promptIds),
        supabase
          .from('prompt_likes')
          .select('prompt_id')
          .in('prompt_id', promptIds),
        supabase
          .from('prompt_likes')
          .select('prompt_id')
          .in('prompt_id', promptIds)
          .eq('user_id', toDatabaseValue(userId))
      ]);

      // Count comments and likes per prompt
      const commentCounts = new Map<string, number>();
      commentsResult.data?.forEach(c => {
        const id = c.prompt_id;
        commentCounts.set(id, (commentCounts.get(id) || 0) + 1);
      });

      const likeCounts = new Map<string, number>();
      likesResult.data?.forEach(l => {
        const id = l.prompt_id;
        likeCounts.set(id, (likeCounts.get(id) || 0) + 1);
      });

      const userLikedSet = new Set(userLikesResult.data?.map(l => l.prompt_id) || []);

      // Order prompts by saved order (most recently saved first)
      const promptOrderMap = new Map(savedData.map((s, i) => [s.prompt_id, i]));
      
      // DEFENSIVE: Normalize all fields to prevent null crashes
      const formattedPrompts: Prompt[] = promptsData
        .sort((a, b) => (promptOrderMap.get(a.id) || 0) - (promptOrderMap.get(b.id) || 0))
        .map(prompt => {
          const profile = profilesMap.get(prompt.user_id);
          const badges = profile?.badges;
          return {
            id: prompt.id,
            title: prompt.title || '',
            description: prompt.description || '',
            content: prompt.content || '',
            // DEFENSIVE: Ensure category is never null/undefined
            category: (prompt.category || 'general-chat') as Exclude<Category, 'all'>,
            categoryId: prompt.category_id,
            userId: prompt.user_id,
            createdAt: prompt.created_at || new Date().toISOString(),
            username: profile?.username || 'Anonymous',
            avatarId: profile?.avatar_id || 1,
            badges: Array.isArray(badges) ? badges : [],
            likes: likeCounts.get(prompt.id) || 0,
            comments: commentCounts.get(prompt.id) || 0,
            isLiked: userLikedSet.has(prompt.id),
            isSaved: true, // These are all saved prompts
            isRemix: prompt.is_remix || false,
            remixOf: prompt.remix_of,
            copyCount: prompt.copy_count || 0,
            geoMetadata: prompt.geo_metadata as any,
          };
        });

      if (reset || pageNum === 0) {
        setPrompts(formattedPrompts);
      } else {
        setPrompts(prev => [...prev, ...formattedPrompts]);
      }

      setHasMore(savedData.length === PAGE_SIZE);

    } catch (error) {
      console.error('Error fetching saved prompts:', error);
      toast.error('Failed to load saved prompts');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSavedPrompts(nextPage);
    }
  }, [loadingMore, hasMore, page, fetchSavedPrompts]);

  const refresh = useCallback(() => {
    setPage(0);
    setHasMore(true);
    fetchSavedPrompts(0, true);
  }, [fetchSavedPrompts]);

  const removePrompt = useCallback((promptId: string) => {
    setPrompts(prev => prev.filter(p => p.id !== promptId));
    setTotalCount(prev => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    if (userId) {
      fetchSavedPrompts(0, true);
    }
  }, [userId, fetchSavedPrompts]);

  return {
    prompts,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh,
    removePrompt,
    totalCount,
  };
};
